import React, { useEffect, useRef, useState } from "react";
import Nav from "@/components/Nav";
import { api, apiError, logDev } from "@/lib/api";
import { toast } from "sonner";
import { Users, Activity, Flag, DollarSign, Crown, Zap, Radio, ShieldAlert, Gavel, AlertTriangle, MessageSquareText } from "lucide-react";

const SEVERITY_COLOR = { high: "text-rose-400", medium: "text-amber-400" };
function reportCardClass(r, flash) {
  if (flash[r.id]) return "bg-rose-500/15 border-rose-500/50";
  if (r.severity === "high") return "bg-rose-500/5 border-rose-500/30";
  return "bg-slate-900/50 border-white/10";
}

export default function Admin() {
  const [tab, setTab] = useState("moderation");
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [live, setLive] = useState(null);
  const [flash, setFlash] = useState({});
  const sinceRef = useRef(null);
  const seenRef = useRef(new Set());

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/reports").then((r) => setReports(r.data.reports)).catch((e) => toast.error(apiError(e)));
  };

  useEffect(() => {
    loadAll();
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/live", { params: sinceRef.current ? { since: sinceRef.current } : {} });
        const fresh = data.reports.filter((r) => sinceRef.current && !seenRef.current.has(r.id));
        data.reports.forEach((r) => seenRef.current.add(r.id));
        if (fresh.length) {
          fresh.forEach((r) => toast.warning(`New report: ${r.reason} → ${r.target_name}`, { duration: 6000 }));
          setFlash((f) => ({ ...f, ...Object.fromEntries(fresh.map((r) => [r.id, true])) }));
          setTimeout(() => setFlash({}), 4000);
        }
        sinceRef.current = data.server_time;
        setLive(data);
      } catch (e) { logDev("live poll failed", e); }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, []);

  const act = async (id, action) => {
    try {
      await api.post("/admin/moderate", { report_id: id, action });
      toast.success(`Action: ${action}`);
      setLive((l) => (l ? { ...l, reports: l.reports.filter((r) => r.id !== id), pending: Math.max(0, l.pending - 1) } : l));
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const pendingCount = live ? live.pending : reports.filter((r) => r.status === "pending").length;

  return (
    <div className="min-h-screen orbit-bg-grid">
      <Nav />
      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <span className="orbit-chip !bg-fuchsia-500/10 !border-fuchsia-500/40 !text-fuchsia-300">ADMIN</span>
        </div>
        <h1 className="font-display text-4xl font-extrabold">Mission control</h1>
        <p className="text-slate-400 mt-1">Metrics, moderation, and user oversight.</p>

        <div className="flex gap-2 border-b border-white/10 mt-8 mb-6">
          <button onClick={() => setTab("moderation")} className={`px-4 py-2 text-sm border-b-2 transition flex items-center gap-2 ${tab === "moderation" ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400"}`} data-testid="admin-tab-moderation">
            Moderation {pendingCount > 0 && <span className="bg-rose-500 text-white text-[10px] font-mono rounded-full px-1.5 py-0.5" data-testid="admin-pending-badge">{pendingCount}</span>}
          </button>
          <button onClick={() => setTab("analytics")} className={`px-4 py-2 text-sm border-b-2 transition ${tab === "analytics" ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400"}`} data-testid="admin-tab-analytics">Analytics</button>
        </div>

        {tab === "analytics" && stats && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <MetricCard icon={Users} label="Total users" value={stats.metrics.total_users} />
              <MetricCard icon={Activity} label="DAU" value={stats.metrics.dau} tone="cyan" />
              <MetricCard icon={Zap} label="Live now" value={live ? live.online : "…"} tone="emerald" />
              <MetricCard icon={DollarSign} label="Revenue / mo" value={`₹${stats.metrics.revenue_month.toFixed(2)}`} tone="fuchsia" />
              <MetricCard icon={Users} label="MAU" value={stats.metrics.mau} />
              <MetricCard icon={Flag} label="Reports" value={stats.metrics.reports} tone="rose" />
              <MetricCard icon={Crown} label="Premium users" value={stats.metrics.premium_users} tone="fuchsia" />
              <MetricCard icon={Activity} label="Skip rate" value={`${Math.round(stats.metrics.skip_rate * 100)}%`} />
            </div>
            <div className="orbit-glass rounded-2xl p-6">
              <div className="text-xs font-mono uppercase text-slate-500 mb-4">Geographic distribution</div>
              <div className="space-y-2">
                {stats.geo.length === 0 && <div className="text-sm text-slate-500">No geo data yet.</div>}
                {stats.geo.map((g) => (
                  <div key={g.country} className="flex items-center gap-3">
                    <div className="w-32 text-sm text-slate-300">{g.country}</div>
                    <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-cyan-400 to-fuchsia-500" style={{ width: `${Math.min(100, (g.count / (stats.metrics.total_users || 1)) * 100)}%` }} />
                    </div>
                    <div className="w-10 text-right font-mono text-xs text-slate-400">{g.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === "moderation" && (
          <div className="space-y-8">
            {/* Live feed */}
            <section className="orbit-glass rounded-3xl p-6 border border-rose-500/20" data-testid="admin-live-feed">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2 font-display text-lg font-bold"><Radio className="w-4 h-4 text-rose-400 orbit-live-dot" /> Live report feed</div>
                <div className="flex items-center gap-2 text-xs font-mono">
                  <span className="orbit-glass rounded-full px-3 py-1 text-emerald-300" data-testid="admin-live-online">{live ? live.online : "…"} online</span>
                  <span className="orbit-glass rounded-full px-3 py-1 text-cyan-300" data-testid="admin-live-incall">{live ? live.in_call : "…"} in calls</span>
                  <span className={`orbit-glass rounded-full px-3 py-1 ${live && live.online >= 10 ? "text-amber-300" : "text-slate-400"}`}>{live && live.online >= 10 ? "PEAK" : "quiet"}</span>
                  <span className="orbit-glass rounded-full px-3 py-1 text-rose-300" data-testid="admin-live-pending">{pendingCount} pending</span>
                  {live?.high > 0 && <span className="rounded-full px-3 py-1 bg-rose-500 text-white font-bold animate-pulse" data-testid="admin-live-high">{live.high} HIGH</span>}
                </div>
              </div>
              {(!live || live.reports.length === 0) && <div className="text-sm text-slate-500 py-6 text-center">No pending reports. The queue is clear.</div>}
              <div className="space-y-2">
                {live?.reports.map((r) => (
                  <div key={r.id} className={`rounded-2xl p-4 flex items-center gap-4 flex-wrap border transition-colors duration-700 ${reportCardClass(r, flash)}`} data-testid={`live-report-${r.id}`}>
                    <AlertTriangle className={`w-5 h-5 shrink-0 ${SEVERITY_COLOR[r.severity] || "text-slate-500"}`} />
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <SeverityBadge severity={r.severity} id={r.id} />
                        <span className="orbit-chip !text-rose-300 !border-rose-500/40 !bg-rose-500/10 uppercase !text-xs">{r.reason}</span>
                        <span className="text-sm font-semibold" data-testid={`live-report-target-${r.id}`}>{r.target_name}</span>
                        {r.target_online && <span className="text-[10px] font-mono text-emerald-300">● online now</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">reported by {r.reporter_name} · {new Date(r.created_at).toLocaleTimeString()}{r.note ? ` · "${r.note}"` : ""}</div>
                      {r.flags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`live-flags-${r.id}`}>
                          {r.flags.map((f) => <span key={f.category} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-200">AUTO · {f.category}: {f.matches.join(", ")}</span>)}
                        </div>
                      )}
                      <Evidence lines={r.evidence} targetId={r.target_id} id={r.id} />
                      {r.frame_base64 && <FrameEvidence b64={r.frame_base64} id={r.id} />}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => act(r.id, "dismiss")} className="orbit-chip" data-testid={`live-dismiss-${r.id}`}>Dismiss</button>
                      <button onClick={() => act(r.id, "warn")} className="orbit-chip !text-amber-300 !border-amber-500/40 inline-flex items-center gap-1" data-testid={`live-warn-${r.id}`}><ShieldAlert className="w-3 h-3" /> Warn</button>
                      <button onClick={() => act(r.id, "ban")} className="orbit-chip !text-rose-300 !border-rose-500/40 !bg-rose-500/10 inline-flex items-center gap-1" data-testid={`live-ban-${r.id}`}><Gavel className="w-3 h-3" /> Ban</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Full history */}
            <section>
              <div className="text-xs font-mono uppercase text-slate-500 mb-3">All reports</div>
              <div className="space-y-2">
                {reports.length === 0 && <div className="orbit-glass rounded-2xl p-8 text-center text-slate-500">No reports yet. Nice and quiet.</div>}
                {reports.map((r) => (
                  <div key={r.id} className="orbit-glass rounded-2xl p-5" data-testid={`mod-report-${r.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="orbit-chip !text-rose-300 !border-rose-500/40 !bg-rose-500/10 uppercase !text-xs">{r.reason}</span>
                          <span className={`orbit-chip !text-xs ${r.status === "pending" ? "!text-amber-300 !border-amber-500/40 !bg-amber-500/10" : "!text-slate-400"}`} data-testid={`mod-status-${r.id}`}>{r.status}</span>
                        </div>
                        <div className="text-sm mt-2 text-slate-300">Target: <span className="font-semibold">{r.target_name}</span> · Reporter: {r.reporter_name}</div>
                        {r.note && <div className="text-xs text-slate-500 mt-1">"{r.note}"</div>}
                        <div className="text-xs font-mono text-slate-500 mt-1">{new Date(r.created_at).toLocaleString()}</div>
                        <Evidence lines={r.evidence} targetId={r.target_id} id={`all-${r.id}`} />
                        {r.frame_base64 && <FrameEvidence b64={r.frame_base64} id={`all-${r.id}`} />}
                      </div>
                      {r.status === "pending" && (
                        <div className="flex gap-2">
                          <button onClick={() => act(r.id, "dismiss")} className="orbit-chip" data-testid={`mod-dismiss-${r.id}`}>Dismiss</button>
                          <button onClick={() => act(r.id, "warn")} className="orbit-chip !text-amber-300 !border-amber-500/40" data-testid={`mod-warn-${r.id}`}>Warn</button>
                          <button onClick={() => act(r.id, "suspend")} className="orbit-chip !text-orange-300 !border-orange-500/40" data-testid={`mod-suspend-${r.id}`}>Suspend</button>
                          <button onClick={() => act(r.id, "ban")} className="orbit-chip !text-rose-300 !border-rose-500/40 !bg-rose-500/10" data-testid={`mod-ban-${r.id}`}>Ban</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function SeverityBadge({ severity, id }) {
  const s = { high: "bg-rose-500 text-white", medium: "bg-amber-500/20 text-amber-300 border border-amber-500/40", low: "bg-slate-800 text-slate-400" }[severity] || "bg-slate-800 text-slate-400";
  return <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full uppercase ${s}`} data-testid={`live-severity-${id}`}>{severity}</span>;
}

function Evidence({ lines = [], targetId, id }) {
  const [open, setOpen] = useState(false);
  if (!lines.length) return <div className="text-[10px] font-mono text-slate-600 mt-1">no chat evidence</div>;
  return (
    <div className="mt-2">
      <button onClick={() => setOpen((o) => !o)} className="text-xs font-mono text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid={`evidence-toggle-${id}`}>
        <MessageSquareText className="w-3 h-3" /> {open ? "Hide" : "Show"} chat evidence ({lines.length} line{lines.length === 1 ? "" : "s"})
      </button>
      {open && (
        <div className="mt-2 rounded-xl bg-slate-950/70 border border-white/10 p-3 space-y-1 max-w-xl" data-testid={`evidence-lines-${id}`}>
          {lines.map((l, i) => (
            <div key={`${l.from_id}-${i}-${l.text.slice(0, 12)}`} className="text-xs flex gap-2">
              <span className={`font-mono shrink-0 ${l.from_id === targetId ? "text-rose-300" : "text-slate-400"}`}>{l.from_name}{l.from_id === targetId ? " (reported)" : ""}:</span>
              <span className="text-slate-200">{l.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-100", cyan: "text-cyan-300", emerald: "text-emerald-300", fuchsia: "text-fuchsia-300", rose: "text-rose-300" };
  return (
    <div className="orbit-glass rounded-2xl p-5">
      <div className="flex items-center gap-2 text-xs font-mono uppercase text-slate-400"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className={`font-display text-2xl font-bold mt-2 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function FrameEvidence({ b64, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button onClick={() => setOpen((o) => !o)} className="text-xs font-mono text-rose-300 hover:text-rose-200" data-testid={`frame-evidence-toggle-${id}`}>{open ? "Hide" : "Show"} flagged camera frame</button>
      {open && <img src={`data:image/jpeg;base64,${b64}`} alt="Flagged frame" className="mt-2 w-40 rounded-lg border border-rose-500/40 blur-sm hover:blur-0 transition" data-testid={`frame-evidence-img-${id}`} />}
    </div>
  );
}
